import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Table, Button, Drawer, Select } from 'antd';
import { EyeOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import SplitRouteHistoryDrawer from '../splitOrder/components/SplitRouteHistoryDrawer';
import RoutePlanningReplayPage from '../routeReplay/RoutePlanningReplayPage';
import { getAssignTypeLabel } from '../splitOrder/utils/assignTypeLabel';
import dayjs, { Dayjs } from 'dayjs';
import { t } from '../../../i18n';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { GET_AREAS_BY_WAREHOUSE_ID } from '../../../graphql/queries/getAreasByWarehouseId';
import { useAllDspsForWmsPermission } from '../accounts/graphql/queries/getAllDspsForWmsPermission';
import { GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID } from '../tasks/graphql/queries/getAvailableDriversByWarehouseId';
import { useCurrentDsp } from '../../../hooks/useCurrentDsp';

import { formatISODateTime } from '../../../utils/dateTimeUtils';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../utils/languageUtils';
import ParcelFilterStyle from '../components/ParcelFilterStyle';
import DateTimeRangeFilterItem from '../components/DateTimeRangeFilterItem';
import {
  LIST_PUSH_HISTORY_PAGE,
  LIST_PUSH_HISTORY_PAGE_FOR_WMS,
  ListPushHistoryPageItem,
} from './graphql/pushHistory';
import {
  LIST_ROUTE_PLANNING_ORDERS_BY_DSP_TASK_ID,
  ListRoutePlanningOrdersByDspTaskIdResponse,
  ListRoutePlanningOrdersByDspTaskIdVariables,
} from './graphql/listRoutePlanningOrdersByDspTaskId';

interface AreaOption {
  id: number;
  name: string;
}

interface DspOption {
  id: number;
  name: string;
}

interface DriverOption {
  accountId: number;
  name: string;
  phone: string;
}

const normalizeText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

type RoutesHistoryPageMode = 'wms' | 'dsp';

interface RoutesHistoryPageProps {
  mode?: RoutesHistoryPageMode;
}

const RoutesHistoryPage: React.FC<RoutesHistoryPageProps> = ({ mode }) => {
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ? Number(currentWarehouse.id) : 0;

  const isDsp = mode === 'dsp';
  const { dspId: currentDspId, dsp: currentDsp } = useCurrentDsp();

  const [taskDateRange, setTaskDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('day'),
    dayjs().startOf('day'),
  ]);
  const [areaId, setAreaId] = useState<number | undefined>(undefined);
  const [dspId, setDspId] = useState<number | undefined>(undefined);
  const [driverId, setDriverId] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // 区域列表
  const { data: areasData, loading: areasLoading } = useQuery(GET_AREAS_BY_WAREHOUSE_ID, {
    variables: { warehouseId: warehouseId || undefined },
    skip: !warehouseId,
  });

  const areaOptions: AreaOption[] = useMemo(
    () =>
      (areasData?.areaList as any[] | undefined)?.map((a) => ({
        id: Number(a.id),
        name: a.name,
      })) || [],
    [areasData],
  );

  const warehouseIdForDspQuery = useMemo(() => {
    return warehouseId ? String(warehouseId) : undefined;
  }, [warehouseId]);

  const { allDspsForWmsPermission: dspsData, loading: dspsLoading } = useAllDspsForWmsPermission(
    warehouseIdForDspQuery,
    isDsp,
  );

  const dspOptions: DspOption[] = useMemo(
    () =>
      dspsData?.map((d: any) => ({
        id: Number(d.id),
        name: d.name,
      })) || [],
    [dspsData],
  );

  const { data: driversData, loading: driversLoading } = useQuery(GET_AVAILABLE_DRIVERS_BY_WAREHOUSE_ID, {
    variables: {
      warehouseId: warehouseId || undefined,
      dspId: dspId ?? null,
    },
    skip: !warehouseId,
  });

  const driverOptions: DriverOption[] = useMemo(
    () =>
      driversData?.getAvailableDriversByWarehouseId?.map((d: any) => ({
        accountId: Number(d.accountId),
        name: d.name,
        phone: d.phone || '',
      })) || [],
    [driversData],
  );

  // ──────────── 推送历史: ListPushHistoryPage（服务端分页 + 服务端过滤） ────────────
  const effectiveDspId = isDsp
    ? (currentDspId != null ? Number(currentDspId) : null)
    : (dspId ?? null);

  const historyQuery = mode === 'wms' ? LIST_PUSH_HISTORY_PAGE_FOR_WMS : LIST_PUSH_HISTORY_PAGE;

  const { data: pushHistoryData, loading: dataLoading } = useQuery(historyQuery, {
    variables: {
      request: {
        warehouseId,
        startTaskDate: taskDateRange ? taskDateRange[0].format('YYYY-MM-DD') : '',
        endTaskDate: taskDateRange ? taskDateRange[1].format('YYYY-MM-DD') : '',
        areaId: areaId ?? null,
        dspId: effectiveDspId,
        driverId: driverId ?? null,
        pageNum: currentPage,
        pageSize,
      },
    },
    skip: !mode || !warehouseId || !taskDateRange,
    fetchPolicy: 'network-only',
  });

  const historyResult = mode === 'wms'
    ? (pushHistoryData as any)?.listPushHistoryPage
    : (pushHistoryData as any)?.listPushHistoryPageForDsp;
  const tableData: ListPushHistoryPageItem[] = historyResult?.content ?? [];
  const total = historyResult?.total ?? 0;

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [selectedDspTaskId, setSelectedDspTaskId] = useState<number | null>(null);
  const [historyDrawerVisible, setHistoryDrawerVisible] = useState(false);
  const [historyDspTaskId, setHistoryDspTaskId] = useState<number | null>(null);
  const [historyAreaId, setHistoryAreaId] = useState<number | null>(null);
  // 分享链接打开历史抽屉时的 warehouseId（deep link 用 URL 的，覆盖当前仓库上下文）
  const [historyWarehouseId, setHistoryWarehouseId] = useState<number | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const historyDeepLinkHandledRef = useRef(false);

  // 路径规划回放：DSP 视图不开放；WMS 侧再按资源码控制（后端 currentUserResourceCodes 下发）
  const canReplay = !isDsp && hasResource([ResourceCode.menu.WHS_DELIVERY_ROUTE_REPLAY]);
  // 路径规划回放：从父行（区域）进入的全屏 Drawer
  const [replayVisible, setReplayVisible] = useState(false);
  const [replayParentDspTaskId, setReplayParentDspTaskId] = useState<number | null>(null);
  const [replayAreaName, setReplayAreaName] = useState<string | undefined>(undefined);
  const replayDeepLinkHandledRef = useRef(false);
  // 打开回放：设置 state 并把定位参数写入 URL（复制链接即可分享）
  const openReplay = useCallback((record: ListPushHistoryPageItem) => {
    if (record.dspTaskId == null) return;
    setReplayParentDspTaskId(record.dspTaskId);
    setReplayAreaName(record.taskName ?? undefined);
    setReplayVisible(true);
    const params = new URLSearchParams(searchParams);
    params.set('replayParentDspTaskId', String(record.dspTaskId));
    if (record.taskName) params.set('replayAreaName', record.taskName); else params.delete('replayAreaName');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  // 关闭回放：清掉 URL 定位参数，避免刷新又弹
  const closeReplay = useCallback(() => {
    setReplayVisible(false);
    const params = new URLSearchParams(searchParams);
    params.delete('replayParentDspTaskId');
    params.delete('replayAreaName');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  // 分享链接：URL 带 replayParentDspTaskId 时自动打开回放（仅处理一次）
  useEffect(() => {
    if (replayDeepLinkHandledRef.current) return;
    const idStr = searchParams.get('replayParentDspTaskId');
    if (!idStr) return;
    replayDeepLinkHandledRef.current = true;
    if (!canReplay) return; // 无回放权限：分享链接也不自动打开
    const id = Number(idStr);
    if (!Number.isFinite(id) || id <= 0) return;
    setReplayParentDspTaskId(id);
    setReplayAreaName(searchParams.get('replayAreaName') ?? undefined);
    setReplayVisible(true);
  }, [searchParams]);

  // 打开历史抽屉：设置 state 并把定位参数写入 URL（便于复制链接分享）
  const openHistoryDrawer = useCallback((dspTaskId: number | null, areaId: number | null, whId?: number) => {
    setHistoryDspTaskId(dspTaskId);
    setHistoryAreaId(areaId);
    setHistoryWarehouseId(whId);
    setHistoryDrawerVisible(true);
    const params = new URLSearchParams(searchParams);
    if (dspTaskId != null) params.set('historyDspTaskId', String(dspTaskId)); else params.delete('historyDspTaskId');
    if (areaId != null) params.set('historyAreaId', String(areaId)); else params.delete('historyAreaId');
    if (whId != null) params.set('historyWarehouseId', String(whId)); else params.delete('historyWarehouseId');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // 关闭历史抽屉：清掉 URL 上的定位参数，避免刷新又弹
  const closeHistoryDrawer = useCallback(() => {
    setHistoryDrawerVisible(false);
    const params = new URLSearchParams(searchParams);
    params.delete('historyDspTaskId');
    params.delete('historyAreaId');
    params.delete('historyWarehouseId');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // 分享链接：URL 带 historyDspTaskId 时自动打开历史抽屉（仅处理一次）
  useEffect(() => {
    if (historyDeepLinkHandledRef.current) return;
    const dspTaskIdStr = searchParams.get('historyDspTaskId');
    if (!dspTaskIdStr) return;
    historyDeepLinkHandledRef.current = true;
    const dspTaskId = Number(dspTaskIdStr);
    if (!Number.isFinite(dspTaskId) || dspTaskId <= 0) return;
    setHistoryDspTaskId(dspTaskId);
    setHistoryAreaId(Number(searchParams.get('historyAreaId')) || null);
    setHistoryWarehouseId(Number(searchParams.get('historyWarehouseId')) || undefined);
    setHistoryDrawerVisible(true);
  }, [searchParams]);

  /** 统一的行 key 生成，确保 rowKey / toggleExpand / expander 三处一致 */
  const getRowKey = useCallback((record: ListPushHistoryPageItem, index?: number) => {
    if (record.dspTaskId != null) return String(record.dspTaskId);
    return `${record.taskDate ?? ''}_${record.taskName ?? ''}_${record.dspAssignedTime ?? ''}_${index ?? 0}`;
  }, []);

  const canExpand = useCallback((record: ListPushHistoryPageItem) => {
    return (record.childTaskList?.length ?? 0) > 0;
  }, []);

  const toggleExpand = useCallback((record: ListPushHistoryPageItem, index?: number) => {
    if (!canExpand(record)) return;
    const key = getRowKey(record, index);
    setExpandedRowKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, [canExpand, getRowKey]);

  const { data: orderListData, loading: orderListLoading } = useQuery<
    ListRoutePlanningOrdersByDspTaskIdResponse,
    ListRoutePlanningOrdersByDspTaskIdVariables
  >(LIST_ROUTE_PLANNING_ORDERS_BY_DSP_TASK_ID, {
    variables: selectedDspTaskId != null ? { dspTaskId: selectedDspTaskId } : (undefined as any),
    skip: !orderDrawerOpen || selectedDspTaskId == null,
    fetchPolicy: 'network-only',
  });

  const openOrdersDrawer = useCallback((record: ListPushHistoryPageItem) => {
    if (!record.dspTaskId) return;
    setSelectedDspTaskId(record.dspTaskId);
    setOrderDrawerOpen(true);
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().startOf('day');
    setTaskDateRange([today, today]);
    setAreaId(undefined);
    setDspId(undefined);
    setDriverId(undefined);
    setCurrentPage(1);
    setPageSize(10);
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const baseColumnWeights = {
    taskDate: 110,
    taskName: 100,
    totalParcels: 100,
    assignType: 110,
    dspName: 120,
    driverName: 110,
    workIndex: 100,
    dspAssignedTime: 170,
    action: 120,
  };

  const makeColumns = useCallback(
    (expanderWeight: number, mode: 'parent' | 'child'): ColumnsType<ListPushHistoryPageItem> => {
      const weights = { expander: expanderWeight, ...baseColumnWeights };
      const totalWeight = Object.values(weights).reduce((a, b) => a + b, 0);
      const cw = (w: number) => `calc(100% * ${w} / ${totalWeight})`;

      return [
        {
          title: '',
          dataIndex: '__expander__',
          key: '__expander__',
          width: cw(weights.expander),
          align: 'center' as const,
          render: (_: unknown, record: ListPushHistoryPageItem, index: number) => {
            if (mode === 'child') {
              return null;
            }
            const disabled = !canExpand(record);
            const key = getRowKey(record, index);
            const expanded = expandedRowKeys.includes(key);
            return (
              <Button
                type="text"
                size="small"
                disabled={disabled}
                onClick={() => toggleExpand(record, index)}
                style={{ fontWeight: 700, opacity: disabled ? 0.35 : 1 }}
              >
                {expanded ? '-' : '+'}
              </Button>
            );
          },
        },
        {
          title: t('delivery.routesHistory.table.taskDate'),
          dataIndex: 'taskDate',
          key: 'taskDate',
          width: cw(weights.taskDate),
          render: (value: string | null) => normalizeText(value),
        },
        {
          title: t('delivery.routesHistory.table.area'),
          dataIndex: 'taskName',
          key: 'taskName',
          width: cw(weights.taskName),
          render: (value: string | null) => normalizeText(value),
        },
        {
          title: t('delivery.routesHistory.table.totalParcels'),
          dataIndex: 'totalParcels',
          key: 'totalParcels',
          width: cw(weights.totalParcels),
          render: (value: number | null, record: ListPushHistoryPageItem) => {
            const text = normalizeText(value);
            const hasSubRoutes = (record.childTaskList?.length ?? 0) > 0;
            const clickable =
              record.dspTaskId != null && value != null && value > 0 && !hasSubRoutes;
            if (!clickable) {
              return text;
            }
            return (
              <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => openOrdersDrawer(record)}>
                {text}
              </Button>
            );
          },
        },
        {
          title: t('delivery.routesHistory.table.assignType'),
          dataIndex: 'assignType',
          key: 'assignType',
          width: cw(weights.assignType),
          render: (value: number | null) => getAssignTypeLabel(value),
        },
        {
          title: t('delivery.routesHistory.table.dsp'),
          dataIndex: 'dspName',
          key: 'dspName',
          width: cw(weights.dspName),
          render: (value: string | null) => normalizeText(value),
        },
        {
          title: t('delivery.routesHistory.table.driver'),
          dataIndex: 'driverName',
          key: 'driverName',
          width: cw(weights.driverName),
          render: (value: string | null, record: any) => {
            if (record.driverId != null && Number(record.driverId) < 0) {
              return <span className="text-red-500">{t('delivery.tasks.label.unassignedDriver')}</span>;
            }
            if (!value) return '-';
            const driver = record.driverId
              ? driverOptions.find((d) => d.accountId === Number(record.driverId))
              : driverOptions.find((d) => d.name === value);
            if (driver?.phone) {
              return `${value} (${driver.phone})`;
            }
            return value;
          },
        },
        {
          title: t('delivery.routesHistory.table.workIndex'),
          dataIndex: 'workIndex',
          key: 'workIndex',
          width: cw(weights.workIndex),
          render: (value: string | null) => normalizeText(value || null),
        },
        {
          title: t('delivery.routesHistory.table.dspAssignedTime'),
          dataIndex: 'dspAssignedTime',
          key: 'dspAssignedTime',
          width: cw(weights.dspAssignedTime),
          render: (value: string | null) => {
            const text = value ? formatISODateTime(value) : '-';
            if (mode === 'child') {
              return (
                <span className="routes-history-child-dsp-assigned-time">{text}</span>
              );
            }
            return text;
          },
        },
        {
          title: '',
          key: 'action',
          width: cw(weights.action),
          render: (_: unknown, record: ListPushHistoryPageItem) => {
            if (mode === 'child' || !record.dspTaskId) return null;
            return (
              <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  style={{ color: '#4f46e5', padding: 0 }}
                  onClick={() => openHistoryDrawer(
                    record.dspTaskId!,
                    areaOptions.find(a => a.name === record.taskName)?.id ?? null,
                    warehouseId,
                  )}
                >
                  {t('delivery.routesHistory.table.viewResult')}
                </Button>
                {canReplay && (
                  <Button
                    type="link"
                    size="small"
                    icon={<SearchOutlined />}
                    style={{ color: '#4f46e5', padding: 0 }}
                    onClick={() => openReplay(record)}
                  >
                    {t('delivery.routeReplay.title')}
                  </Button>
                )}
              </span>
            );
          },
        },
      ];
    },
    [canExpand, canReplay, driverOptions, expandedRowKeys, getRowKey, openOrdersDrawer, t, toggleExpand],
  );

  /** 父表：加减号列宽度为原来一半，便于与子行数据列对齐 */
  const parentColumns = useMemo(() => makeColumns(28, 'parent'), [makeColumns]);
  /** 子表：展开列占位保持原比例，子行不用动 */
  const childColumns = useMemo(() => makeColumns(56, 'child'), [makeColumns]);

  const labelTaskDateRange = String(t('delivery.routesHistory.filter.taskDateRange'));
  const labelArea = String(t('delivery.routesHistory.filter.area'));
  const labelDsp = String(t('delivery.routesHistory.filter.dsp'));
  const labelDriver = String(t('delivery.routesHistory.filter.driver'));
  const colLabels = [labelTaskDateRange, labelArea, labelDsp, labelDriver];

  // 司机筛选器的样式 hooks（需要在组件顶层调用）
  const driverContainerClassName = useFilterContainerClassName();
  const driverLabelClassName = useFilterLabelClassName(getSmartLabelClassName(colLabels));
  const driverInputContainerClassName = useFilterInputContainerClassName();

  const handleTaskDateRangeChange = (dates: [string | null, string | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setTaskDateRange(null);
      return;
    }
    setTaskDateRange([dayjs(dates[0]), dayjs(dates[1])]);
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="flex items-center space-x-4" style={{ marginBottom: 16 }}>
        <div className="border-l-4 border-indigo-600 pl-2 text-xl font-medium">
          {t('delivery.routesHistory.title')}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
        <style>{`
          .routes-history-child-row td {
            background: #fafafa !important;
          }
          .routes-history-child-dsp-assigned-time {
            display: inline-block;
            padding-left: 20%;
          }
        `}</style>
        <div className="mb-2 pl-0 py-2">
          <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
            <div className="min-w-0">
              <DateTimeRangeFilterItem
                label={labelTaskDateRange}
                value={taskDateRange ? [taskDateRange[0].format('YYYY-MM-DD'), taskDateRange[1].format('YYYY-MM-DD')] : null}
                onChange={handleTaskDateRangeChange}
                format="YYYY-MM-DD"
                showTime={false}
                allowClear={false}
                spanClassName={getSmartLabelClassName(colLabels)}
              />
            </div>
            <div className="min-w-0">
              <ParcelFilterStyle
                label={labelArea}
                value={areaId != null ? String(areaId) : undefined}
                onChange={(value) => { setAreaId(value ? Number(value) : undefined); setCurrentPage(1); }}
                options={areaOptions.map((a) => ({ label: a.name, value: a.id }))}
                placeholder={t('components.input.all') || undefined}
                showSearch
                disabled={areasLoading}
                spanClassName={getSmartLabelClassName(colLabels)}
              />
            </div>
            <div className="min-w-0">
              <ParcelFilterStyle
                label={labelDsp}
                value={isDsp && currentDspId != null ? String(Number(currentDspId)) : dspId != null ? String(dspId) : undefined}
                onChange={(value) => { setDspId(value ? Number(value) : undefined); setCurrentPage(1); }}
                options={
                  isDsp && currentDsp
                    ? [{ label: currentDsp.name, value: Number(currentDsp.id) }]
                    : dspOptions.map((d) => ({ label: d.name, value: d.id }))
                }
                placeholder={t('components.input.all') || undefined}
                showSearch
                disabled={isDsp || dspsLoading}
                loading={dspsLoading}
                spanClassName={getSmartLabelClassName(colLabels)}
              />
            </div>
            <div className="min-w-0">
              <div className={driverContainerClassName}>
                <span className={driverLabelClassName}>{labelDriver}</span>
                <div className={driverInputContainerClassName}>
                  <Select
                    value={driverId}
                    onChange={(value) => { setDriverId(value ?? undefined); setCurrentPage(1); }}
                    placeholder={t('components.input.all')}
                    className="w-full"
                    size="middle"
                    allowClear
                    showSearch
                    suffixIcon={<DownOutlined />}
                    disabled={driversLoading}
                    loading={driversLoading}
                    filterOption={(input, option) => {
                      const driver = driverOptions.find((d) => d.accountId === option?.value);
                      if (!driver) return false;
                      const searchText = input.toLowerCase();
                      return (
                        driver.name?.toLowerCase().includes(searchText) ||
                        driver.phone?.toLowerCase().includes(searchText)
                      );
                    }}
                  >
                    {driverOptions.map((driver) => (
                      <Select.Option key={driver.accountId} value={driver.accountId}>
                        {driver.name} {driver.phone ? `(${driver.phone})` : ''}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-4 pt-3">
            <Button onClick={handleReset} className="w-auto min-w-24">
              <span className="font-bold">{t('common.reset')}</span>
            </Button>
            <Button type="primary" onClick={handleSearch} className="w-auto min-w-24">
              <span className="font-bold">{t('common.search')}</span>
            </Button>
          </div>
        </div>

        <Table<ListPushHistoryPageItem>
          rowKey={(record, index) => getRowKey(record, index)}
          columns={parentColumns}
          dataSource={tableData}
          loading={dataLoading}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 980 }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
            rowExpandable: canExpand,
            expandIcon: () => null,
            expandedRowRender: (record) => {
              const children = (record.childTaskList ?? []) as ListPushHistoryPageItem[];
              if (!children.length) return null;
              return (
                <div style={{ paddingLeft: 16 }}>
                  <Table<ListPushHistoryPageItem>
                    rowKey={(r, idx) => String(r.dspTaskId ?? `${record.dspTaskId ?? ''}_${record.taskName ?? ''}_${idx}`)}
                    columns={childColumns}
                    dataSource={children}
                    pagination={false}
                    size="small"
                    showHeader={false}
                    tableLayout="fixed"
                    rowClassName={() => 'routes-history-child-row'}
                  />
                </div>
              );
            },
          }}
        />
        <Drawer
          title={t('delivery.routesHistory.drawer.orderListTitle')}
          placement="right"
          width={420}
          open={orderDrawerOpen}
          onClose={() => setOrderDrawerOpen(false)}
          destroyOnClose
        >
          <Table
            rowKey={(r: any, idx) => String(r.orderId ?? r.trackingNumber ?? idx)}
            loading={orderListLoading}
            pagination={false}
            size="small"
            dataSource={orderListData?.listRoutePlanningOrdersByDspTaskId ?? []}
            columns={[
              {
                title: t('delivery.routesHistory.drawer.stopNumber'),
                dataIndex: 'stopNumber',
                key: 'stopNumber',
                width: 110,
                render: (v: number | null) => normalizeText(v),
              },
              {
                title: t('delivery.routesHistory.drawer.trackingNumber'),
                dataIndex: 'trackingNumber',
                key: 'trackingNumber',
                render: (v: string | null) => normalizeText(v),
              },
            ]}
          />
        </Drawer>
        <div style={{ marginTop: 16 }}>
          <TablePaginationBar
            total={total}
            current={currentPage}
            pageSize={pageSize}
            onChange={handlePageChange}
          />
        </div>
      </div>

      <SplitRouteHistoryDrawer
        visible={historyDrawerVisible}
        dspTaskId={historyDspTaskId}
        warehouseId={historyWarehouseId ?? warehouseId}
        areaId={historyAreaId}
        mode={mode}
        onClose={closeHistoryDrawer}
      />

      <Drawer
        open={replayVisible}
        width="100%"
        closable={false}
        keyboard={false}
        destroyOnClose
        styles={{ body: { padding: 0 } }}
        onClose={closeReplay}
      >
        {replayParentDspTaskId != null && (
          <RoutePlanningReplayPage
            parentDspTaskId={replayParentDspTaskId}
            areaName={replayAreaName}
            onClose={closeReplay}
          />
        )}
      </Drawer>
    </div>
  );
};

export default RoutesHistoryPage;
